import { possessive } from '@shared';
import {
  AdvisorCustomDisclosures,
  Citations,
  OnboardingLayout,
  OnboardingResultsStatusScreen,
  useSlideSetup,
  useResultsAreInSlide,
  citations,
} from '@/features/client-onboarding';
import { OnboardingResultsAreIn, OnboardingOurPredictions, OnboardingCostFactors } from '@/pages';

import advisorImageUrl from '@/images/OnboardingResultsAreIn.png';

export function GenworthOnboardingResultsAreIn({ clientId }: ClientIdProps) {
  const {
    clientFirstName,
    clientHasStartedLtc,
    isAlgorithmFinishedRunning,
    hasAlgorithmError,
    retryCountdown,
    handleRefresh,
  } = useResultsAreInSlide(clientId);

  if (hasAlgorithmError) {
    return (
      <OnboardingResultsStatusScreen
        variant="error"
        onRefresh={handleRefresh}
      />
    );
  }

  if (!isAlgorithmFinishedRunning) {
    return (
      <OnboardingResultsStatusScreen
        variant="pending"
        retryCountdown={retryCountdown}
        onRefresh={handleRefresh}
      />
    );
  }

  return (
    <GenworthResultsAreInContent
      clientFirstName={clientFirstName}
      clientHasStartedLtc={clientHasStartedLtc}
    />
  );
}

interface GenworthResultsAreInContentProps {
  clientFirstName: string;
  clientHasStartedLtc: boolean;
}

function GenworthResultsAreInContent({
  clientFirstName,
  clientHasStartedLtc,
}: GenworthResultsAreInContentProps) {
  // Reuse the canonical results-are-in slide identity; only the copy/CTA differ for Genworth.
  useSlideSetup({
    slidePage: OnboardingResultsAreIn,
    nextButtonLabel: clientHasStartedLtc
      ? `See ${possessive(clientFirstName)} Costs`
      : `See Stats from People Like ${clientFirstName}`,
    nextSlidePage: clientHasStartedLtc ? OnboardingCostFactors : OnboardingOurPredictions,
  });

  return (
    <OnboardingLayout>
      <div className="flex h-max flex-col items-center justify-center pb-4">
        <div className="grid grid-cols-1 items-center gap-2 pb-6 pt-0 md:grid-cols-2 md:py-16">
          <img
            src={advisorImageUrl}
            className="md:max-w-100 w-full max-w-48 md:mx-auto"
            alt="Results are in"
          />
          <div className="mb-4 flex flex-col gap-3 leading-normal tracking-wide md:gap-5">
            <h1
              data-testid="results-are-in-header"
              className="text-balance text-2xl font-bold text-gray-950 md:text-4xl"
            >
              {possessive(clientFirstName)} insights are in!
            </h1>
            <p
              className="text-sm md:text-xl"
              data-testid="results-are-in-description"
            >
              {possessive(clientFirstName)} long-term care insights come from{' '}
              <span className="text-primary">Genworth policyholders</span> who look similar to{' '}
              {clientFirstName}.
            </p>
            <p className="text-sm md:text-xl">
              We compare {possessive(clientFirstName)} profile like age, gender, location, and marital
              status, with similar policyholders to surface relevant long-term care stats.
            </p>
          </div>
        </div>
        <div className="mt-auto pt-4">
          <Citations
            className="w-full"
            citations={[citations.OnboardingResultsAreInDisclaimer]}
          />
          <AdvisorCustomDisclosures className="w-full" />
        </div>
      </div>
    </OnboardingLayout>
  );
}
